/**
 * ============================================================================
 * 新闻内容 - News Content
 * ============================================================================
 *
 * 【作用】控制“新闻动态”页的标题、副标题，以及新闻列表；featured: true 的条目会在首页轮播显示。
 * 【如何修改】页面标题改 title/subtitle；增删新闻在 items 数组中添加或删除对象。
 *
 * 【单条新闻参数说明】
 * - id: 唯一数字
 * - date: 日期字符串，如 "2025-01-15"
 * - title / summary: { en: "...", cn: "..." }；列表和弹窗摘要区均用 summary
 * - category: 分类名，如 "Research" | "Award" | "Team" | "News"
 * - featured: true 时出现在首页轮播
 * - image: 图片路径，如 "/images/news/xxx.jpg"（文件放 public/images/news/）
 * - details: { en: { abstract?, author, link, fullText }, cn: { ... } }
 *   - abstract: 可选，摘要/导语；平时可注释掉不填，则弹窗只显示 summary + fullText
 *   - fullText: 详情正文
 *   - author / link: 可选
 */

export const newsContent = {
  title: { en: "Latest News", cn: "最新动态" },
  subtitle: { en: "Stay updated with our latest discoveries and achievements", cn: "了解我们最新的发现和进展" },
  items: [
    {
      id: 4,
      date: "2026-02-24",
      title: { 
        en: "Our Laboratory Website is Launched", 
        cn: "实验室网站正式上线" 
      },
      summary: { 
        en: "Our laboratory website is officially launched! Welcome to visit!", 
        cn: "我们的实验室网站正式上线啦，欢迎访问！" 
      },
      category: "News",
      featured: true,
      // image: "/images/news/xxx.jpg",  // 此处可填写本条新闻的图片路径
      details: {
        en: {
          // abstract: "此处可填写摘要/导语；若存在则弹窗在 summary 与 fullText 之间显示",
          author: "Boyu Qie",
          link: "https://www.boyuqie.com",
          fullText: `Our laboratory website is officially launched, welcome to visit! The current website address is https://www.boyuqie.com. We will keep updating it, so please stay tuned!

          The logic and process of building this website are as follows:

          1. The project initially used Google Gemini 3 Pro to automatically generate the basic web development framework, quickly finishing the prototypes for the homepage and content sections.
          2. Then, modern responsive design principles were adopted (such as two-column + grid layouts, mobile-first), and practical style libraries like Tailwind CSS were used to optimize the interface style and animation effects.
          3. The frontend of the website is mainly developed using React (JSX), while content management and multilingual support are implemented through custom hooks and separate content configuration files, making future expansion and maintenance easy.
          4. All source code has been uploaded to GitHub, with detailed development notes and comments available in the corresponding repository (repository address: https://github.com/boyuqie/Homepage).
          5. For building and deployment, we currently plan to use Cloudflare Pages (if you have better suggestions, feel free to discuss!), which enables automated CI/CD, branch previews, and fast online deployment capabilities.

          Additional notes:
          - We welcome any suggestions regarding website construction—feel free to discuss at any time!
          - If you want to use some templates from this site, or want to learn more about the development and deployment process, please also contact us. The repository is open, and all issues and PRs are welcome for discussion.`
        },
        cn: {
          // abstract: "此处可填写中文摘要/导语",
          author: "郄博宇",
          link: "https://www.boyuqie.com",
          fullText: `我们实验室网站正式上线，欢迎访问！目前的网址是https://www.boyuqie.com。之后将会不断更新，请关注！

          本网站的搭建逻辑如下：

          1. 项目最初通过Google Gemini 3 pro自动生成了基础网页开发框架，快速完成了首页及内容板块的原型设计。
          2. 接下来，采用了现代响应式设计体系（如双栏+栅格布局，移动端优先），并借助了“Tailwind CSS”等实用样式库优化界面风格与动画效果。
          3. 网站前端主要采用React（JSX）进行开发，内容管理和多语言支持则通过自定义hooks和独立内容配置文件实现，便于后期扩展和维护。
          4. 所有源码均已托管至GitHub，详细开发与注释可在对应repo查看（仓库地址：https://github.com/boyuqie/Homepage）。
          5. 网站的构建与部署目前计划使用Cloudflare Pages（后续如有更好建议欢迎交流），可以实现分支预览、快速上线等现代部署能力。
          
          补充说明：
          - 欢迎有网页建设建议的朋友随时交流探讨！
          - 如果你希望借用本网站的部分模板，或想详细了解开发与部署流程，也欢迎和我们联系～仓库开放，任何issue和PR都欢迎讨论。`
        }
      }
    },
    {
      id: 3,
      date: "2026-02-15",
      title: { 
        en: "Our paper on flat-band Kagome Aza-triangulene COF is accepted by Nature Materials", 
        cn: "我们关于笼目型Aza-triangulene COF的论文被Nature Materials接收" 
      },
      summary: { 
        en: "We design, synthesize, and measure a new Kagome Aza-triangulene COF with phase-frustration-induced flat bands near the Fermi level, and the paper is accepted by Nature Materials.", 
        cn: "我们设计，合成并测量了一种在费米能级附近具有相位阻挫诱导的平带的基于Aza-triangulene分子的双笼目结构COF，目前该论文已被Nature Materials接收。" 
      },
      category: "Research",
      featured: true,
      // image: "/images/news/xxx.jpg",  // 此处可填写本条新闻的图片路径
      details: {
        en: {
          // abstract: "此处可填写英文导语；取消注释后在 summary 与 fullText 之间显示",
          author: "Boyu Qie",
          //link: "https://www.nature.com/articles/...",
          //fullText: "The paper describes our novel machine learning framework that predicts optimal synthesis conditions for organic quantum materials. By analyzing thousands of reaction pathways, our AI system can identify the most promising candidates in days rather than months."
        },
        cn: {
          // abstract: "此处可填写中文导语",
          author: "郄博宇",
          //link: "https://www.nature.com/articles/...",
          //fullText: "论文描述了我们预测有机量子材料最优合成条件的新型机器学习框架。通过分析数千条反应路径，我们的AI系统可以在几天而不是几个月内识别最有希望的候选材料。"
        }
      }
    },
    {
      id: 2,
      date: "2026-02-09", 
      title: { 
        en: "Our Pseudo-Jahn-Teller Distortion paper is accepted by JACS", 
        cn: "我们研究赝姜-泰勒畸变的论文被JACS接收" 
      },
      summary: { 
        en: "We discovered ground state symmetry breaking induced by pseudo-Jahn-Teller distortion in a surface-synthesized one-dimensional carbon nanoribbon.", 
        cn: "我们在表面合成的一维碳材料纳米带中，发现了赝姜-泰勒畸变引起的基态对称性破缺现象" 
      },
      category: "Research",
      featured: true,
      image: "/images/news/news2.jpeg",
      details: {
        en: {
          author: "Boyu Qie",
          link: "https://pubs.acs.org/doi/abs/10.1021/jacs.5c21950",
          fullText: "Structural distortions in low-dimensional π-conjugated systems profoundly influence their electronic properties, but the control of such behavior in laterally extended systems remains challenging. Here we demonstrate that a one-dimensional conjugated polymer─poly-(difluorenoheptalene-ethynylene) (PDFHE)─undergoes a pronounced out-of-plane backbone distortion, equivalent to a spontaneous symmetry breaking (SSB) of its mirror symmetry. We synthesized PDFHE on noble metal surfaces and characterized its structure and electronic states using low-temperature scanning tunneling microscopy (STM). Rather than adopting a planar, high-symmetry conformation, PDFHE relaxes into nonplanar isomers stabilized by a pseudo–Jahn–Teller (PJT) distortion having mirror-odd out-of-plane character. The distortion lowers the total energy and increases the band gap, providing a concise rationale for the observed symmetry breaking. Density functional theory calculations corroborate these findings, providing a microscopic explanation for the SSB. Our results show that even in mechanically robust extended π-systems, subtle electron–lattice coupling can spontaneously drive significant structural rearrangements."
        },
        cn: {
          author: "郄博宇",
          link: "https://pubs.acs.org/doi/abs/10.1021/jacs.5c21950",
          fullText: "在低维π共轭体系中，结构畸变对材料的电子性质有极为深远的影响，但对于结构较为扩展的体系，这类畸变的控制和理解一直颇具挑战。我们的最新研究表明，一种一维共轭高分子——聚(二芴庚稀炔烯)（PDFHE）会自发发生明显的面外骨架畸变，本质上对应了镜面对称性的自发破缺。我们在贵金属表面合成了PDFHE，并利用低温扫描隧道显微镜（STM）对其结构和电子态进行了详细分析。实验发现，这一高分子在能量上更倾向于形成非平面、低对称性的异构体，其稳定性来自于赝姜-泰勒效应所驱动的面外扰动，这种畸变不仅降低了体系总能，还显著拓宽了带隙，合理解释了观测到的对称性破缺现象。密度泛函理论（DFT）计算进一步印证了这些结果，并从微观角度揭示了对称性破缺的机理。这项工作表明，即便在结构较为坚固、尺度延展的π体系中，精细的电子-晶格耦合也能自发引起深远的结构重组。"
        }
      }
    },
    {
      id: 1,
      date: "2026-01-01",
      title: { 
        en: "New PhD Student Mingchen Gao Joins the Lab", 
        cn: "新博士生高铭辰加入实验室" 
      },
      summary: { 
        en: "Bringing expertise in AI for science and will contribute to the development of organic quantum materials", 
        cn: "将人工智能应用到科学中，并开始新型有机量子材料理论的研究" 
      },
      category: "Team",
      featured: true,
      // image: "/images/news/xxx.jpg",  // 此处可填写本条新闻的图片路径
      details: {
        en: {
          author: "Lab Announcement",
          link: null,
          fullText: "Mingchen completed his undergraduate studies in Applied Physics at Southeast University, and obtained his M.S. in Materials Science and Engineering from SUSTech. He has a strong background in physics and AI, and is interested in the theory of organic quantum materials. He will start his PhD studies in Q-Lab in 2026, and will contribute to the development of novel databases and ML models for predicting material properties, as well as the theory of organic quantum materials. His research interests are perfectly aligned with our lab's mission to accelerate quantum materials discovery through AI."
        },
        cn: {
          author: "实验室公告",
          link: null,
          fullText: "高铭辰在东南大学完成了应用物理本科学业，并且在南科大量子院获得了材料与化工硕士学位。他在物理和人工智能方面有丰富的背景知识，并且对有机量子材料理论有浓厚的兴趣。他将于2026年加入Q-Lab开始博士阶段的研究与学习，将致力于开发用于预测材料性质的新型数据库和ML模型。他的研究兴趣与我们实验室通过AI加速量子材料发现的目标完美契合。"
        }
      }
    }
  ]
};
